import React from 'react';
import { useState, useEffect } from 'react';
import { httpRequest, httpRequestUtil } from '../api/helper';
import { Box, Button } from '@mui/material';
import { useNotification } from '../context/NotificationContext';
import { useAccount } from '../context/AccountContext';
import { AuthContext } from '../context/AuthContext';
import { FINAL_SUBSCRIPTION_PRICE } from '../common/AppConstants';
import { PRODUCT_NAME } from '../common/AppConstants';
import MaterialSymbol from '../components/MaterialSymbol';
import { API_ERROR_MESSAGE, AUTH_URLS } from '../common/AppConstants';
import logger from '../logger';

const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
}

export default function RazorpayPaymentButton({ button_text = "Checkout"}) {
  const { addNotification } = useNotification();
  const { loggedIn } = React.useContext(AuthContext);
  const { account, fetchAccountData } = useAccount();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');

  const handlePayment = async (amount, currency = 'INR') => {
    setLoading(true);
    setPaymentStatus('');
    const productDescription = 'Premium Subscription';

    try {
      // Create order on backend
      const orderResponse = await httpRequestUtil('POST', '/billing/create-order/', {
        amount: amount,
        currency: currency
      });

      if (orderResponse.status === 201) {
        const { id, order_id } = orderResponse.data.entry;

        // Razorpay checkout options
        const options = {
          order_id: order_id,
          amount: amount * 100, // Convert to smallest unit (paise)
          currency: currency,
          key: razorpayKey,
          name: PRODUCT_NAME,
          description: productDescription,
          handler: async function (razorpayResponse) {
            // console.log(razorpayResponse)
            // Payment successful - verify on backend

            await httpRequest({
              method: 'PUT',
              url: '/billing/verify-payment/',
              data: {
                id: id,
                order_id: razorpayResponse.razorpay_order_id,
                payment_id: razorpayResponse.razorpay_payment_id,
                signature: razorpayResponse.razorpay_signature
              },
              onSuccess: (data) => {
                fetchAccountData();
                setPaymentStatus('Payment successful!');
              },
              onFailure: (data) => {
                setPaymentStatus('Payment verification failed!');
              },
              addNotification,
            });
          },
          prefill: {
            name: account?.user?.first_name,
            email: account?.user?.email,
            contact: account?.user?.phone || "", // Important, otherwise razorpay asks user to manually enter
          },
          theme: {
            color: '#233836'  // #3399cc
          },
          modal: {
            ondismiss: function () {
              setPaymentStatus('Payment cancelled by user');
              setLoading(false);
            }
          }
        };

        const res = await loadRazorpayScript();
        if (!res) {
          addNotification("Razorpay SDK failed to load", "error");
          return;
        }

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', function (razorpayResponse) {
          setPaymentStatus('Payment failed!');
          addNotification(`Payment failed: ${razorpayResponse.error.description}`, 'error');
          console.error('Payment failed:', razorpayResponse.error);
        });

        razorpay.open();
      } else {
        if (orderResponse.status === 429) {
          addNotification(API_ERROR_MESSAGE.clientError429, 'warning');
        } else if (orderResponse.status >= 400 && orderResponse.status < 500) {
          if (orderResponse.status === 400 && orderResponse.data.code === "already_subscribed") {
            addNotification( orderResponse.data.detail || 'Already subscribed to premium plan', 'success');
          }
          else if (orderResponse.status === 401) {
            const validation_error =
              orderResponse.data.detail || 'Wrong credentials provided';
            logger.log('Wrong credentials provided', validation_error);
            addNotification(validation_error, 'warning');
          } else {
            addNotification(API_ERROR_MESSAGE.clientError, 'warning');
          }
        } else if (orderResponse.status >= 500) {
          addNotification(API_ERROR_MESSAGE.serverError, 'error');
        } else {
          addNotification(API_ERROR_MESSAGE.error, 'error');
        }
      }
    } catch (error) {
      logger.error('Payment error:', error);
      setPaymentStatus('Error initiating payment');
      addNotification('Error initiating payment. Please try again.', 'error');
    } finally {
      setLoading(false);
      // alert(paymentStatus);
    }
  };

  return (
    <Box
      sx={{
        // padding: '20px',
        maxWidth: '400px',
      }}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={() => handlePayment(FINAL_SUBSCRIPTION_PRICE)}
        disabled={loading}
        size="small"
        startIcon={<MaterialSymbol name="crown" size={20} fill={1} />}
        sx={{
          px: 4,
          padding: '8px 24px',
          fontSize: '16px',
          backgroundColor: '#0e8a0a', // 3399cc
          // color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? 'Processing...' : button_text }
      </Button>
    </Box>
  );
};
